import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Flower2, Scale, Mountain, Waves, Sun } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const WINE = '#8B2B3A';

const PILLARS = [
  { icon: Flower2, title: 'Propósito', text: 'Lo que te motiva cada día.', bg: '#EADBD3' },
  { icon: Scale, title: 'Equilibrio', text: 'Entre cuerpo, mente y vida.', bg: '#E8C4C4' },
  { icon: Mountain, title: 'Disciplina', text: 'La constancia que te acerca a tu mejor versión.', bg: '#D6DCCB' },
  { icon: Waves, title: 'Conexión', text: 'Contigo misma y con lo que te hace feliz.', bg: '#C9D6E0' },
  { icon: Sun, title: 'Libertad', text: 'Para florecer a tu propio ritmo.', bg: '#E2C1C1' },
];


const IkigaiHero = ({ onBack }) => (
  <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
    <button onClick={onBack} className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-500 hover:text-black transition-colors flex items-center gap-1.5 mb-8" data-testid="ikigai-back-btn">
      <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Inicio
    </button>
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.35em] mb-5" style={{ color: WINE }}>
        Colección 04 · 生き甲斐
      </p>
      <h1 className="text-6xl sm:text-7xl lg:text-8xl leading-[0.9] mb-4" style={{ ...SERIF, color: WINE, fontWeight: 600 }} data-testid="ikigai-title">
        Ikigai
      </h1>
      <p className="text-2xl sm:text-3xl italic text-neutral-700 mb-8" style={SERIF}>
        Entrena con propósito
      </p>
      <span className="inline-block text-white text-[11px] font-bold uppercase tracking-[0.3em] px-6 py-3" style={{ backgroundColor: WINE }} data-testid="ikigai-coming-soon-badge">
        Próximamente
      </span>
    </div>
    <img src="/ikigai-poster.webp" alt="Ikigai Collection — JRF Sportswear" className="w-full max-w-md mx-auto shadow-2xl" data-testid="ikigai-poster" />
    </div>
  </section>
);

const IkigaiStory = () => (
  <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
    <h2 className="text-3xl sm:text-4xl mb-6" style={{ ...SERIF, color: WINE }}>¿Qué es Ikigai?</h2>
    <p className="text-base sm:text-lg text-neutral-700 leading-relaxed mb-14">
      Ikigai es un concepto japonés que significa <strong style={{ color: WINE }}>“razón de ser”</strong>. Es aquello que te motiva, te da energía y hace que cada día tenga un propósito.
    </p>
    <h2 className="text-3xl sm:text-4xl mb-6" style={{ ...SERIF, color: WINE }}>Nuestra Ikigai Collection</h2>
    <p className="text-base sm:text-lg text-neutral-700 leading-relaxed">
      Inspirada en esa filosofía de vida, creada para mujeres que encuentran en el deporte mucho más que una rutina: una forma de <strong style={{ color: WINE }}>crecer, superarse y conectar</strong> con lo que realmente las hace felices.
    </p>
  </section>
);

const IkigaiPillars = () => (
  <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-20">
    <h2 className="text-center text-3xl sm:text-4xl mb-12" style={{ ...SERIF, color: WINE }}>Ikigai representa</h2>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
      {PILLARS.map(({ icon: Icon, title, text, bg }, i) => (
        <div key={title} className="text-center animate-in fade-in slide-in-from-bottom-4 duration-700" style={{ animationDelay: `${i * 80}ms` }} data-testid={`ikigai-pillar-${i}`}>
          <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center transition-transform duration-300 hover:scale-110" style={{ backgroundColor: bg }}>
            <Icon className="w-8 h-8" style={{ color: WINE }} strokeWidth={1.3} />
          </div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] mb-2" style={{ color: WINE }}>{title}</p>
          <p className="text-sm text-neutral-600 leading-snug">{text}</p>
        </div>
      ))}
    </div>
  </section>
);

export default function IkigaiPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F6EFE8' }} data-testid="ikigai-page">
      <Header />
      <main className="flex-1">
        <IkigaiHero onBack={() => navigate('/')} />
        <IkigaiStory />
        <IkigaiPillars />
        <section className="text-center pb-24 px-4">
          <p className="text-3xl sm:text-4xl italic mb-6" style={{ ...SERIF, color: WINE }}>Más que ropa deportiva, una forma de vivir.</p>
          <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-neutral-500">Tu propósito también mueve el mundo · Próximamente</p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
